// Typed API client for mcpgraph-gateway. Every response is validated with Zod
// (schemas.ts), so a malformed/partial payload throws a typed ApiError instead
// of corrupting the UI. Auth is the dashboard user-session: an httpOnly cookie
// is preferred; an in-memory bearer token is supported as a fallback.

import { z } from 'zod';
import { apiUrl, assistantUrl } from '../config';
import {
  AssistantMetricsSummary,
  AssistantModelRoles,
  AssistantCallModels,
  AssistantModels,
  AssistantAdminEngines,
  AssistantMemberEngines,
  AssistantMyDefaultEngine,
  AuthMe,
  AuthMethods,
  Client,
  ClientCreated,
  DeviceLink,
  IssuedToken,
  ClientToken,
  LoginResponse,
  RegisterResponse,
  RegistrationStatus,
  PasswordResetStatus,
  OkResponse,
  OneTimePasswordReset,
  Settings,
  AssistantConfig,
  Network,
  NetworkCreate,
  NetworkPatch,
  NetworkMembership,
  MemberAdd,
  Organization,
  Overview,
  ProviderInstance,
  NodeAdminConfig,
  CancelAll,
  DesiredConfigApply,
  QueueDepth,
  QueueDrain,
  ServiceDefinition,
  ServiceDefinitionCreate,
  ServiceDefinitionPatch,
  ServiceCatalogEntry,
  CatalogSource,
  DockerHubSearchResponse,
  ServiceActivationOut,
  ServicePublishNetworks,
  ServiceKind,
  ServicePublication,
  ObjectUploadResult,
  StreamSession,
  StreamWatchToken,
  McpSession,
  Task,
  TokenGrant,
  GrantPurge,
  TokenSelectors,
  ToolCallResult,
  TestPanel,
  CatalogTool,
  User,
  UserCreate,
  UserPatch,
  AuditEvent,
  CallLog,
  UsageRecord,
  UsageByTool,
  ObjectIndex,
  ObjectLocations,
  PublicLink,
  Route,
  RouteCreate,
  RouteUpdate,
  RouteInstance,
  RouteUsage,
  RouteExercise,
  MetricsSummary,
  Project,
  ProjectMember,
  listOf,
} from './schemas';

const V1 = '/api/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let bearer: string | null = null;
/** Set/clear an in-memory session bearer (cookie auth needs no call). */
export function setSessionToken(token: string | null): void {
  bearer = token;
}

/**
 * AH8 — the dashboard has to NOTICE that its sign-in ended somewhere else.
 *
 * Signing out in the workspace revokes the session this tab's cookie names, and from that moment
 * every call here comes back 401. But auth was bootstrapped once, in a mount effect, so nothing
 * looked at those 401s: the dashboard went on rendering a signed-in shell whose every panel quietly
 * failed. From the user's side that is "sign out did not work" — the surface they left open still
 * shows their name.
 *
 * There is no push to build for this. The 401 is already the truth arriving; it just had no one
 * listening. Any unauthorized answer now says so once, and the auth context routes to the sign-in
 * screen — the "honest 401 path", which needs no front channel and cannot go stale.
 */
type SessionEndedListener = () => void;
const sessionEndedListeners = new Set<SessionEndedListener>();

export function onSessionEnded(fn: SessionEndedListener): () => void {
  sessionEndedListeners.add(fn);
  return () => sessionEndedListeners.delete(fn);
}

function announceSessionEnded(): void {
  // The bearer (if any) died with the session; keeping it would send a dead credential forever.
  bearer = null;
  for (const fn of [...sessionEndedListeners]) {
    try {
      fn();
    } catch {
      /* one listener's failure must not swallow the notice for the rest */
    }
  }
}

interface ReqOpts {
  method?: string;
  body?: unknown;
  // When true a 401 does not throw (used by /auth/me bootstrap).
  allowUnauthorized?: boolean;
}

// NOTE: constrain on the schema type and return z.infer<S> (the OUTPUT type).
// The server (Pydantic) serializes optional/unset fields as explicit `null`, but our Zod schemas use
// `.optional()` / `.default()` which only fill `undefined`. Recursively drop null-valued keys so absent and
// null are treated identically (matches how the UI uses these fields); nullable schemas still get undefined.
function stripNulls<T>(v: T): T {
  if (Array.isArray(v)) return v.map((x) => stripNulls(x)) as unknown as T;
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      if (val !== null) out[k] = stripNulls(val);
    }
    return out as unknown as T;
  }
  return v;
}

// Using `z.ZodType<T>` would let TS unify T to the INPUT type when a schema has
// .default()/.catch() (yielding optional/unknown fields) — we always want the
// validated OUTPUT shape.
async function request<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  opts: ReqOpts = {},
): Promise<z.infer<S>> {
  return (await requestWithHeaders(path, schema, opts)).data;
}

// Same contract as `request()`, but also hands back the response headers — the paged task list
// carries its next-page cursor in `X-Next-Cursor`, not in the (backward-compatible) array body.
async function requestWithHeaders<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  opts: ReqOpts = {},
): Promise<{ data: z.infer<S>; headers: Headers }> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`;

  // Cookie (same-origin session) vs bearer (cross-origin embedded GUI):
  // when we hold a bearer we authenticate purely via the Authorization header,
  // so cookies are unnecessary. Sending `credentials:'include'` cross-origin
  // would require the server to echo a specific Origin + Allow-Credentials:true
  // (an `Access-Control-Allow-Origin: *` response is REJECTED by the browser in
  // credentialed mode). Omitting credentials when a bearer is present keeps the
  // wildcard-CORS gateway working for the Tauri-embedded dashboard.
  const credentials: RequestCredentials = bearer ? 'omit' : 'include';

  const res = await fetch(apiUrl(path), {
    method: opts.method ?? 'GET',
    headers,
    credentials,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401) {
    // Both branches announce: the bootstrap's own 401 (`allowUnauthorized`) is how a tab opened
    // AFTER the sign-out learns it, and every other 401 is how a tab already open learns it.
    announceSessionEnded();
    if (opts.allowUnauthorized) throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => '');
    }
    let msg = `HTTP ${res.status}`;
    if (body && typeof body === 'object' && 'message' in body) {
      msg = String((body as { message: unknown }).message);
    }
    throw new ApiError(res.status, msg, body);
  }

  // 204 No Content / empty body.
  if (res.status === 204) return { data: schema.parse(undefined as unknown), headers: res.headers };
  const json = await res.json();
  const parsed = schema.safeParse(stripNulls(json));
  if (!parsed.success) {
    throw new ApiError(res.status, `response schema mismatch for ${path}`, parsed.error.issues);
  }
  return { data: parsed.data, headers: res.headers };
}

/** One page of `GET /tasks`: light rows + the opaque cursor of the next page (null = last page). */
export interface TaskPage {
  items: Task[];
  total?: number;
  nextCursor: string | null;
}

// AF6 — a minimal GET+Zod fetch for the workspace-assistant metrics endpoint. It
// targets `assistantUrl` (the `/assistant-api/` reverse-proxy) instead of the gateway
// base, and needs no auth (the summary is aggregate-only). Same stripNulls + safeParse
// contract as `request()` so a malformed/partial payload throws a typed ApiError.
async function assistantFetch<S extends z.ZodTypeAny>(path: string, schema: S): Promise<z.infer<S>> {
  const res = await fetch(assistantUrl(path), { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
  const json = await res.json();
  const parsed = schema.safeParse(stripNulls(json));
  if (!parsed.success) {
    throw new ApiError(res.status, `response schema mismatch for ${path}`, parsed.error.issues);
  }
  return parsed.data;
}

// MC3 — an AUTHENTICATED GET/PUT against the workspace-assistant admin REST (via `/assistant-api/`).
// Unlike `assistantFetch` (the unauthenticated metrics summary), the model-role admin endpoints are
// admin-gated by the assistant's gateway-introspection auth, so the caller's bearer must ride along
// (the assistant validates it against the gateway, exactly like the dashboard's own bearer). Same
// stripNulls + safeParse contract as `request()`.
async function assistantRequest<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  opts: { method?: string; body?: unknown } = {},
): Promise<z.infer<S>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
  const res = await fetch(assistantUrl(path), {
    method: opts.method ?? 'GET',
    headers,
    // Bearer => omit cookies (cross-origin wildcard CORS); otherwise send the same-origin session.
    credentials: bearer ? 'omit' : 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => '');
    }
    let msg = `HTTP ${res.status}`;
    if (body && typeof body === 'object' && 'detail' in body) msg = String((body as { detail: unknown }).detail);
    else if (body && typeof body === 'object' && 'message' in body) msg = String((body as { message: unknown }).message);
    throw new ApiError(res.status, msg, body);
  }
  const parsed = schema.safeParse(stripNulls(await res.json()));
  if (!parsed.success) {
    throw new ApiError(res.status, `response schema mismatch for ${path}`, parsed.error.issues);
  }
  return parsed.data;
}

const Empty = z.unknown();

// --- auth/session --------------------------------------------------------

export const api = {
  // auth
  login: (email: string, password: string) =>
    request(`${V1}/auth/login`, LoginResponse, { method: 'POST', body: { email, password } }),
  logout: () => request(`${V1}/auth/logout`, Empty, { method: 'POST' }),
  me: () => request(`${V1}/auth/me`, AuthMe, { allowUnauthorized: true }),
  ssoUrl: (provider: 'gitlab' | 'google') => apiUrl(`${V1}/auth/sso/${provider}`),

  // AH4 — mint a one-time "sign in on another device" link for MY account. The dashboard is served
  // from the authorization server's own origin, so the session cookie already sent with every call
  // here is the browser principal the endpoint wants; nothing extra is needed. The response's `url`
  // carries the token in its FRAGMENT and is therefore a credential: it goes into a QR code drawn
  // in this tab and nowhere else — never a log, never a query string, never an <img src>.
  createDeviceLink: () =>
    request(`${V1}/auth/device-link`, DeviceLink, { method: 'POST' }),

  // AH3 — which sign-in methods this deployment actually has. One unauthenticated round-trip that
  // answers for password / register / reset / SSO at once; the login screen renders from it and
  // therefore can no longer offer a provider the server would 503 on.
  authMethods: () => request(`${V1}/auth/methods`, AuthMethods, { allowUnauthorized: true }),

  // Public self-registration. Both endpoints are reachable while logged out, so
  // a 401 must not throw here (mirror api.me()).
  registrationStatus: () =>
    request(`${V1}/auth/registration`, RegistrationStatus, { allowUnauthorized: true }),
  // On success the server sets the session cookie AND returns the login shape, so
  // the caller is immediately authenticated. 403 = registration disabled, 409 =
  // email already exists.
  // EV1: or a 202 `{verification_required: true, email}` with NO session when the gateway requires
  // e-mail confirmation — the caller shows "check your mail" instead of entering the app.
  register: (email: string, password: string, display_name?: string) =>
    request(`${V1}/auth/register`, RegisterResponse, {
      method: 'POST',
      body: display_name ? { email, password, display_name } : { email, password },
    }),

  // "Forgot password" flow (WN12). The capability probe drives the login link; the request endpoint
  // ALWAYS succeeds (202, no user-enumeration) so the UI shows the same "if that email exists…"
  // notice either way. Both are reachable while logged out, so a 401 must not throw here.
  passwordResetStatus: () =>
    request(`${V1}/auth/password-reset`, PasswordResetStatus, { allowUnauthorized: true }),
  passwordResetRequest: (email: string) =>
    request(`${V1}/auth/password-reset/request`, Empty, {
      method: 'POST',
      body: { email },
      allowUnauthorized: true,
    }),
  // Consume the emailed token + set the new password. 400 = invalid/expired/used token (or a
  // too-short password); 404 = the feature is disabled.
  passwordResetConfirm: (token: string, new_password: string) =>
    request(`${V1}/auth/password-reset/confirm`, OkResponse, {
      method: 'POST',
      body: { token, new_password },
      allowUnauthorized: true,
    }),

  // Admin settings (admin session). Read + toggle the public-registration flag.
  getSettings: () => request(`${V1}/settings`, Settings),
  updateSettings: (body: { registration_enabled: boolean }) =>
    request(`${V1}/settings`, Settings, { method: 'PATCH', body }),

  // BF15/BF18 — the assistant's instance config: default reply LANGUAGE + the GLOBAL memory
  // layer. The GET is open to any authenticated user (the assistant service reads it under the
  // caller's bearer); every write below is admin-only server-side.
  getAssistantConfig: () => request(`${V1}/assistant/config`, AssistantConfig),
  // CL17 — the catalogue behind the call-model picker.
  getAssistantModels: () => request(`${V1}/assistant/models`, AssistantCallModels),
  updateAssistantConfig: (body: {
    language?: string;
    global_memory?: { id: string; text: string }[];
    // CL17. "" is a MEANINGFUL value — "revert to the deployment default" — so it must be sendable.
    call_model?: string;
  }) =>
    request(`${V1}/assistant/config`, AssistantConfig, { method: 'PATCH', body }),
  addGlobalMemory: (text: string) =>
    request(`${V1}/assistant/memory`, AssistantConfig, { method: 'POST', body: { text } }),
  deleteGlobalMemory: (noteId: string) =>
    request(`${V1}/assistant/memory/${encodeURIComponent(noteId)}`, AssistantConfig, {
      method: 'DELETE',
    }),

  // overview
  overview: () => request(`${V1}/overview`, Overview),

  // ST40 — Prometheus-metrics summary (p50/p95 per tool, saturation, VRAM headroom,
  // counters) aggregated over a window. Same auth as the rest of /api/v1.
  metricsSummary: (windowSeconds?: number) => {
    const qs = windowSeconds != null ? `?${new URLSearchParams({ window_seconds: String(windowSeconds) })}` : '';
    return request(`${V1}/metrics/summary${qs}`, MetricsSummary);
  },

  // AF6 — workspace-assistant per-turn metrics summary. This lives on a DIFFERENT
  // service than the gateway, reached via the same-origin `/assistant-api/` prefix
  // (reverse-proxied by nginx / the vite dev proxy to the assistant's /api). The
  // endpoint is unauthenticated (aggregate stats only), so this uses a dedicated
  // fetch rather than the gateway `request()` helper (which targets `apiUrl`).
  assistantMetricsSummary: (windowSeconds?: number) => {
    const qs = windowSeconds != null ? `?${new URLSearchParams({ window_seconds: String(windowSeconds) })}` : '';
    return assistantFetch(`/assistant-api/v1/metrics/summary${qs}`, AssistantMetricsSummary);
  },

  // MC3 — model-role admin (workspace-assistant, via /assistant-api/, admin-gated). Read the
  // role→model config, write overrides (or reset to default), and read the LIVE text-llm model list
  // that populates the dropdown. All carry the caller's bearer (see assistantRequest).
  getModelRoles: () =>
    assistantRequest(`/assistant-api/v1/admin/model-roles`, AssistantModelRoles),
  putModelRoles: (
    updates: { role: string; model?: string | null; allowlist?: string[] | null; reset?: boolean }[],
  ) =>
    assistantRequest(`/assistant-api/v1/admin/model-roles`, AssistantModelRoles, {
      method: 'PUT',
      body: { roles: updates },
    }),
  getModels: () => assistantRequest(`/assistant-api/v1/admin/models`, AssistantModels),

  // AE11 — engine A/B (workspace-assistant, via /assistant-api/, admin-gated). Read every registered
  // engine with its default flag + live health + capabilities, and retarget the runtime default
  // engine WITHOUT a restart (new conversations bind to it; existing ones keep their engine).
  getAdminEngines: () =>
    assistantRequest(`/assistant-api/v1/admin/engines`, AssistantAdminEngines),
  setDefaultEngine: (engine: string | null) =>
    assistantRequest(`/assistant-api/v1/admin/engines/default`, AssistantAdminEngines, {
      method: 'PUT',
      body: { engine },
    }),

  // MEMBER engines (workspace-assistant, via /assistant-api/, member-gated — cookie or bearer). The
  // list a member may pick from (names + default flag + `selectable`), and their PER-USER default
  // engine (read + set; `engine:null` reverts to the org/global default). No health/capabilities —
  // those stay admin-only (see getAdminEngines).
  getMemberEngines: () =>
    assistantRequest(`/assistant-api/v1/engines`, AssistantMemberEngines),
  getMyDefaultEngine: () =>
    assistantRequest(`/assistant-api/v1/engines/default`, AssistantMyDefaultEngine),
  setMyDefaultEngine: (engine: string | null) =>
    assistantRequest(`/assistant-api/v1/engines/default`, AssistantMyDefaultEngine, {
      method: 'PUT',
      body: { engine },
    }),

  // ST43 — Projects (list + members + per-project usage). A gateway predating the
  // Projects API 404s; callers treat that as "no projects".
  projects: () => request(`${V1}/projects`, listOf(Project)),
  project: (id: string) => request(`${V1}/projects/${id}`, Project),
  projectMembers: (id: string) => request(`${V1}/projects/${id}/members`, listOf(ProjectMember)),

  // tenancy
  org: (id: string) => request(`${V1}/orgs/${id}`, Organization),
  users: () => request(`${V1}/users`, listOf(User)),
  // Create a user by email (admin). `password` empty/absent → SSO-only user.
  // Returns the created User (201). Body validated against UserCreate.
  createUser: (body: z.infer<typeof UserCreate>) =>
    request(`${V1}/users`, User, { method: 'POST', body: UserCreate.parse(body) }),
  // Update a user's role/status/display_name (admin). Returns the updated User.
  patchUser: (id: string, body: z.infer<typeof UserPatch>) =>
    request(`${V1}/users/${id}`, User, { method: 'PATCH', body: UserPatch.parse(body) }),
  // Delete a user (admin). 204 on success.
  deleteUser: (id: string) => request(`${V1}/users/${id}`, Empty, { method: 'DELETE' }),
  // OTP1 — reset a user's password to a ONE-TIME password (admin, own org, not self). Ends every
  // sign-in of the user; `revoke_tokens` also kills their PATs / MCP tokens. The response holds the
  // temp password exactly once — show it, never persist it.
  // EV1: `send_email` also mails it to the user (default on the server: "mail is configured");
  // `email_only` keeps it out of this response, the letter being its only copy.
  resetOneTimePassword: (
    id: string,
    body: {
      revoke_tokens?: boolean;
      expires_in_hours?: number;
      send_email?: boolean;
      email_only?: boolean;
      // EV2 — the front the letter links to ('mcpgraph' default | 'workspace' | 'motor').
      app?: 'mcpgraph' | 'workspace' | 'motor';
    } = {},
  ) =>
    request(`${V1}/users/${id}/password/reset-one-time`, OneTimePasswordReset, { method: 'POST', body }),
  // EV1 — admin: confirm a user's address without the link / mail a fresh confirmation link.
  markEmailVerified: (id: string) =>
    request(`${V1}/users/${id}/email/verify`, User, { method: 'POST' }),
  sendEmailVerification: (id: string) =>
    request(`${V1}/users/${id}/email/send-verification`, Empty, { method: 'POST' }),

  // Self-service profile (the signed-in user edits THEIR OWN record).
  // Update the caller's display name. Returns the updated User (refresh /auth/me
  // afterwards so the userbar picks up the new name).
  updateProfile: (body: { display_name: string }) =>
    request(`${V1}/users/me`, User, { method: 'PATCH', body }),
  // Change the caller's password. 204 on success; on a 400 the backend reason
  // (wrong current password / too short / SSO-only account) surfaces as the
  // thrown ApiError's `message` (request() reads `body.message`).
  changePassword: (body: { current_password: string; new_password: string }) =>
    request(`${V1}/users/me/password`, Empty, { method: 'POST', body }),
  networks: () => request(`${V1}/networks`, listOf(Network)),
  network: (id: string) => request(`${V1}/networks/${id}`, Network),
  // Create a network (any user). The caller becomes owner. Body validated
  // against NetworkCreate; response is the created Network (201).
  createNetwork: (body: z.infer<typeof NetworkCreate>) =>
    request(`${V1}/networks`, Network, { method: 'POST', body: NetworkCreate.parse(body) }),
  // Edit a network (owner/admin). Returns the updated Network. 403 = non-owner.
  patchNetwork: (id: string, body: z.infer<typeof NetworkPatch>) =>
    request(`${V1}/networks/${id}`, Network, { method: 'PATCH', body: NetworkPatch.parse(body) }),
  // Delete a network (owner/admin). 204 on success, 403 for a non-owner.
  deleteNetwork: (id: string) => request(`${V1}/networks/${id}`, Empty, { method: 'DELETE' }),
  networkMembers: (id: string) =>
    request(`${V1}/networks/${id}/members`, listOf(NetworkMembership)),
  // Add a member to a network (owner/admin). Body validated against MemberAdd; the
  // response shape isn't relied on (callers refresh the members list), so it is left
  // unvalidated to tolerate the server returning the membership or 204.
  addNetworkMember: (id: string, body: z.infer<typeof MemberAdd>) =>
    request(`${V1}/networks/${id}/members`, Empty, { method: 'POST', body: MemberAdd.parse(body) }),
  // Remove a member from a network (owner/admin). 204 on success, 403 for a non-owner.
  removeNetworkMember: (id: string, userId: string) =>
    request(`${V1}/networks/${id}/members/${userId}`, Empty, { method: 'DELETE' }),

  // services & publications
  serviceDefinitions: () => request(`${V1}/service-definitions`, listOf(ServiceDefinition)),
  serviceDefinition: (id: string) => request(`${V1}/service-definitions/${id}`, ServiceDefinition),
  // The catalog the caller sees: shared active service-defs (admins also see
  // inactive) + the caller's personal defs, each annotated with `active`
  // (admin global flag) and `active_for_me` (the caller's opt-in/out).
  //
  // Backward-compat: a gateway that predates the service-catalog endpoint 404s
  // here — fall back to GET /service-definitions and derive a best-effort catalog
  // so the view still renders (kind/active default conservatively; per-user
  // activation isn't available on such a gateway).
  serviceCatalog: async (): Promise<{ items: ServiceCatalogEntry[]; total?: number }> => {
    try {
      return await request(`${V1}/service-catalog`, listOf(ServiceCatalogEntry));
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 404)) throw e;
      const defs = await request(`${V1}/service-definitions`, listOf(ServiceDefinition));
      const items: ServiceCatalogEntry[] = defs.items.map((d) => ({
        id: d.id,
        name: d.name,
        owner_id: d.owner_id,
        kind: d.kind ?? 'shared',
        active: d.active ?? true,
        active_for_me: true,
        image_ref: d.image_ref,
        spec_hash: d.spec_hash,
        gpu_class: d.gpu_class,
        default_env_schema: d.default_env_schema,
        source: 'db',
        source_kind: 'db',
        description: undefined,
        tags: [],
        created_at: d.created_at,
        tools: d.tools.map((t) => ({
          id: t.id,
          name: t.name,
          schema_hash: t.schema_hash,
          input_schema: t.input_schema,
          output_schema: t.output_schema,
          streaming: t.streaming,
          idempotent: t.idempotent,
          media_type: t.media_type,
        })),
      }));
      return { items, total: items.length };
    }
  },
  // Create a service-definition shell. `kind=shared` is admin-only (403 otherwise);
  // `kind=personal` (default) any user. Tools are registered by the providing
  // client; the dashboard supplies name + image (+ optional GPU floor + spec_hash).
  createServiceDefinition: (
    body: z.infer<typeof ServiceDefinitionCreate>,
    kind: z.infer<typeof ServiceKind> = 'personal',
  ) =>
    request(`${V1}/service-definitions?kind=${kind}`, ServiceDefinition, {
      method: 'POST',
      body: ServiceDefinitionCreate.parse(body),
    }),
  // Edit a service-definition (owner). Returns the updated record. 403 = non-owner.
  patchServiceDefinition: (id: string, body: z.infer<typeof ServiceDefinitionPatch>) =>
    request(`${V1}/service-definitions/${id}`, ServiceDefinition, {
      method: 'PATCH',
      body: ServiceDefinitionPatch.parse(body),
    }),
  // Admin-only global activate/deactivate of a SHARED template (PATCH active).
  // 403 for a non-admin.
  patchServiceActive: (id: string, active: boolean) =>
    request(`${V1}/service-definitions/${id}`, ServiceDefinition, {
      method: 'PATCH',
      body: { active },
    }),
  // The current user opts a SHARED def in/out for themselves. Returns the
  // resulting activation state.
  setServiceActivation: (id: string, is_active: boolean) =>
    request(`${V1}/service-definitions/${id}/activation`, ServiceActivationOut, {
      method: 'POST',
      body: { is_active },
    }),
  // H15: the networks the caller may publish this service into, each flagged with whether the service
  // is currently published (= available) there. Backs the slide-out network panel. A gateway that
  // predates H15 404s — callers treat that as "no per-network publish" and hide the panel.
  publishNetworks: (id: string) =>
    request(`${V1}/service-definitions/${id}/publish-networks`, ServicePublishNetworks),
  // Reconcile the set of networks this service is published to (create-or-approve / withdraw). Applies
  // live; returns the updated panel state.
  setPublishNetworks: (id: string, network_ids: string[]) =>
    request(`${V1}/service-definitions/${id}/publish-networks`, ServicePublishNetworks, {
      method: 'PUT',
      body: { network_ids },
    }),
  // Delete a service-definition (owner). 204 on success, 403 for a non-owner.
  deleteServiceDefinition: (id: string) =>
    request(`${V1}/service-definitions/${id}`, Empty, { method: 'DELETE' }),

  // Catalog sources (H13, admin). List every source (built-ins + custom remotes) with last-fetch
  // status; add/patch/remove a remote source. A gateway predating this endpoint 404s — callers
  // treat that as "no manageable sources" and hide the panel.
  catalogSources: () => request(`${V1}/catalog-sources`, listOf(CatalogSource)),
  createCatalogSource: (body: { name: string; url: string; priority?: number; headers?: Record<string, string> }) =>
    request(`${V1}/catalog-sources`, CatalogSource, { method: 'POST', body }),
  patchCatalogSource: (
    id: string,
    body: { name?: string; url?: string; enabled?: boolean; priority?: number; headers?: Record<string, string> },
  ) => request(`${V1}/catalog-sources/${id}`, CatalogSource, { method: 'PATCH', body }),
  deleteCatalogSource: (id: string) =>
    request(`${V1}/catalog-sources/${id}`, Empty, { method: 'DELETE' }),

  // Docker Hub import (H11). Search Docker Hub's public API by term; import a chosen image as a
  // ServiceDefinition (kind=personal default, shared admin-only). A gateway that predates H11 404s; a
  // gateway host without internet 503s — the import view surfaces either inline.
  dockerhubSearch: (q: string, limit = 25) =>
    request(
      `${V1}/catalog-sources/dockerhub/search?q=${encodeURIComponent(q)}&limit=${limit}`,
      DockerHubSearchResponse,
    ),
  dockerhubImport: (
    body: {
      repo_name: string;
      tag?: string;
      name?: string;
      description?: string;
      tags?: string[];
      port?: number;
      is_official?: boolean;
    },
    kind: z.infer<typeof ServiceKind> = 'personal',
  ) =>
    request(`${V1}/catalog-sources/dockerhub/import?kind=${kind}`, ServiceDefinition, {
      method: 'POST',
      body,
    }),
  // Flat admin list across all networks (filterable by network/status).
  publications: (params?: { network?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.network) q.set('network', params.network);
    if (params?.status) q.set('status', params.status);
    const qs = q.toString();
    return request(`${V1}/publications${qs ? `?${qs}` : ''}`, listOf(ServicePublication));
  },
  // Nested per-network listing (the owner's view).
  networkPublications: (networkId: string) =>
    request(`${V1}/networks/${networkId}/publications`, listOf(ServicePublication)),
  // Create a publication into a network (network owner approves separately).
  createPublication: (
    networkId: string,
    body: {
      service_definition_id: string;
      service_slug: string;
      exposed_tool_ids: string[];
      visibility?: string;
    },
  ) =>
    request(`${V1}/networks/${networkId}/publications`, ServicePublication, {
      method: 'POST',
      body,
    }),
  // Approve/suspend are nested under the owning network (only the network owner
  // may decide). The flat /publications list carries network_id for the caller.
  approvePublication: (networkId: string, pubId: string) =>
    request(`${V1}/networks/${networkId}/publications/${pubId}:approve`, ServicePublication, {
      method: 'POST',
    }),
  suspendPublication: (networkId: string, pubId: string) =>
    request(`${V1}/networks/${networkId}/publications/${pubId}:suspend`, ServicePublication, {
      method: 'POST',
    }),
  // Delete/withdraw a publication (flat route; network owner or publisher). 204 on
  // success, 403 for a non-owner. `pubId` is globally unique so no network segment.
  deletePublication: (pubId: string) =>
    request(`${V1}/publications/${pubId}`, Empty, { method: 'DELETE' }),

  // tool catalog (canonical names + schemas + live providers)
  tools: () => request(`${V1}/tools`, listOf(CatalogTool)),

  // H12: the declarative test panel for a service (auto-derived from tool schemas + the service's
  // runtime_config.test_panel override), each tool carrying its resolved canonical name + live-provider
  // count. A gateway predating H12 404s — the caller treats that as "no panel" and hides the tester.
  testPanel: (sdId: string) =>
    request(`${V1}/service-catalog/${encodeURIComponent(sdId)}/test-panel`, TestPanel),

  // clients / nodes
  clients: () => request(`${V1}/clients`, listOf(Client)),
  client: (id: string) => request(`${V1}/clients/${id}`, Client),
  // Register a client / console node (ANY user). The caller becomes owner and the
  // server MINTS the client's first token, returned ONCE as `client_token`.
  createClient: (body: { name: string; node_profile?: Record<string, unknown>; allowed_networks?: string[] }) =>
    request(`${V1}/clients`, ClientCreated, { method: 'POST', body }),
  clientInstances: (id: string) =>
    request(`${V1}/clients/${id}/instances`, listOf(ProviderInstance)),
  // Delete/deregister a client (owner/admin). 204 on success, 403 for a non-owner.
  deleteClient: (id: string) => request(`${V1}/clients/${id}`, Empty, { method: 'DELETE' }),
  // H8 node-admin: read a node's central config editor data (assignable active-services + GPU fitness +
  // which are live), and set its desired config (active-service selection + limits) — pushed to the
  // node over its live control session. Admin-only on the server.
  nodeDesiredConfig: (id: string) =>
    request(`${V1}/clients/${id}/desired-config`, NodeAdminConfig),
  setNodeDesiredConfig: (
    id: string,
    body: { selected: string[]; enabled?: boolean; limits?: { cpu_concurrency?: number | null; max_credits_per_gpu?: number | null; max_concurrent_jobs?: number | null } },
  ) => request(`${V1}/clients/${id}/desired-config`, DesiredConfigApply, { method: 'PUT', body }),

  // tasks & queues
  // The list is PAGED and LIGHT: rows come without result_ref/arguments_ref (`result_omitted` says a
  // result exists) — the full row is `api.task(id)`. The next page's cursor rides in `X-Next-Cursor`
  // (absent on the last page, and on an older gateway that does not page at all).
  // `scope` (OW1): `own` = the caller's own calls, `networks` = the member-network ledger; omitted =
  // the role's default (what an admin always sends).
  tasks: async (params?: { tool?: string; state?: string; cursor?: string; limit?: number; scope?: 'own' | 'networks' }): Promise<TaskPage> => {
    const q = new URLSearchParams();
    if (params?.scope) q.set('scope', params.scope);
    if (params?.tool) q.set('tool', params.tool);
    if (params?.state) q.set('state', params.state);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.cursor) q.set('cursor', params.cursor);
    const qs = q.toString();
    const { data, headers } = await requestWithHeaders(`${V1}/tasks${qs ? `?${qs}` : ''}`, listOf(Task));
    return { ...data, nextCursor: headers.get('X-Next-Cursor') || null };
  },
  task: (id: string) => request(`${V1}/tasks/${id}`, Task),
  cancelTask: (id: string) => request(`${V1}/tasks/${id}:cancel`, Task, { method: 'POST' }),
  // Cancel every in-flight (QUEUED/DISPATCHED/RUNNING) task the caller can see.
  cancelAllTasks: () => request(`${V1}/tasks:cancel-all`, CancelAll, { method: 'POST' }),
  queues: () => request(`${V1}/queues`, listOf(QueueDepth)),
  // Drain a tool's queue: cancel every QUEUED (not-yet-dispatched) task for it.
  drainQueue: (tool: string) =>
    request(`${V1}/queues/${encodeURIComponent(tool)}:drain`, QueueDrain, { method: 'POST' }),

  // tokens & grants (admin)
  // UX21 — machine-minted grants (task:/stream:/route:, one per dispatch) are hidden
  // by the gateway unless asked for. `includeMachine` is what the "show system tokens"
  // toggle passes; a gateway that predates the filter simply ignores the parameter and
  // returns everything, which is what it did before.
  grants: (includeMachine = false) =>
    request(`${V1}/tokens${includeMachine ? '?include_machine=true' : ''}`, listOf(TokenGrant)),
  /** Delete every DEAD machine-minted grant of the caller in one call (UX21). */
  purgeGrants: () => request(`${V1}/tokens/purge`, GrantPurge, { method: 'POST', body: {} }),
  clientTokens: () => request(`${V1}/tokens/client`, listOf(ClientToken)),
  issueMcpToken: (body: {
    user_id: string;
    scopes: string[];
    tool_selectors: z.infer<typeof TokenSelectors>;
    network_ids: string[];
    audience?: string;
    expiry?: string;
  }) => request(`${V1}/tokens/mcp-access`, IssuedToken, { method: 'POST', body }),
  issueClientToken: (body: { client_id: string; allowed_networks: string[] }) =>
    request(`${V1}/tokens/client`, IssuedToken, { method: 'POST', body }),
  // Since UX21 this DELETES the grant row rather than flagging it revoked.
  revokeToken: (id: string) => request(`${V1}/tokens/${id}`, Empty, { method: 'DELETE' }),

  // objects (distributed object store, §5B). `key` is network-scoped and may
  // contain slashes ({network}/{sha-or-uuid}/{name}); the server takes it as a
  // QUERY/BODY param (never a path segment) so the slashes don't break routing.
  objects: (params?: { network?: string; namespace?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.network) q.set('network', params.network);
    // §5B isolation namespace (e.g. ``project/{id}`` / ``canvas/{sid}``) — scopes the index browse to
    // one project's / session's storage. Backs the Projects "Storage" link.
    if (params?.namespace) q.set('namespace', params.namespace);
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString();
    return request(`${V1}/objects${qs ? `?${qs}` : ''}`, ObjectIndex);
  },
  // Upload a file into a network's object store (multipart FormData). The JSON
  // request() helper can't carry multipart, so this calls fetch directly: NO
  // Content-Type header (the browser sets the multipart boundary) and credentials
  // are included for the session cookie. Validates the FROZEN upload envelope.
  uploadObject: async (
    file: File,
    network: string,
    name?: string,
  ): Promise<z.infer<typeof ObjectUploadResult>> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('network', network);
    if (name) fd.append('name', name);
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
    const res = await fetch(apiUrl(`${V1}/objects/upload`), {
      method: 'POST',
      headers,
      // Match request(): bearer => omit cookies (cross-origin wildcard CORS),
      // otherwise include the same-origin session cookie.
      credentials: bearer ? 'omit' : 'include',
      body: fd,
    });
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = await res.text().catch(() => '');
      }
      let msg = `HTTP ${res.status}`;
      if (body && typeof body === 'object' && 'message' in body) {
        msg = String((body as { message: unknown }).message);
      }
      throw new ApiError(res.status, msg, body);
    }
    const parsed = ObjectUploadResult.safeParse(stripNulls(await res.json()));
    if (!parsed.success) {
      throw new ApiError(res.status, 'response schema mismatch for /objects/upload', parsed.error.issues);
    }
    return parsed.data;
  },
  // Browser-downloadable URL for an object — the gateway STREAMS the bytes same-origin
  // (H51/H43): no 302 to an internal-minio presigned host (unreachable from the browser).
  // Reachable for both stored {network}/… keys and bare node-lib refs. Opened in a new tab
  // (download) or used as an <img>/<audio>/<video> src (inline preview); cookie auth carries.
  objectDownloadUrl: (key: string) =>
    apiUrl(`${V1}/objects/download?${new URLSearchParams({ key }).toString()}`),
  // Delete an object (all replicas) by key. Key is a QUERY param (keys contain '/').
  // 204 on success, 403 for a non-owner.
  deleteObject: (key: string) =>
    request(`${V1}/objects?${new URLSearchParams({ key }).toString()}`, Empty, { method: 'DELETE' }),
  objectLocations: (key: string) =>
    request(`${V1}/objects/locations?${new URLSearchParams({ key }).toString()}`, ObjectLocations),
  // Ensure a copy on the network's durable global tier. FROZEN response is the
  // full replica set after replication: {s3_key, locations:[<entry>]}.
  replicateObject: (key: string) =>
    request(`${V1}/objects/replicate`, ObjectLocations, { method: 'POST', body: { key } }),
  // Mint a TTL signed public URL (auto-replicates to the global tier first).
  objectPublicLink: (key: string, ttlSeconds?: number) =>
    request(`${V1}/objects/public-link`, PublicLink, {
      method: 'POST',
      body: ttlSeconds != null ? { key, ttl_seconds: ttlSeconds } : { key },
    }),

  // routes (network stream pipelines, ADR-020)
  routes: (networkId: string, includeVersions = false) =>
    request(
      `${V1}/networks/${networkId}/routes${includeVersions ? '?include_versions=true' : ''}`,
      listOf(Route),
    ),
  route: (networkId: string, name: string, version?: number) =>
    request(
      `${V1}/networks/${networkId}/routes/${encodeURIComponent(name)}${version != null ? `?version=${version}` : ''}`,
      Route,
    ),
  routeVersions: (networkId: string, name: string) =>
    request(`${V1}/networks/${networkId}/routes/${encodeURIComponent(name)}/versions`, listOf(Route)),
  // Create a route (v1). 400 on an invalid chain (out-of-network / non-streaming /
  // incompatible channel types), 403 without the network editor role, 409 if it exists.
  createRoute: (networkId: string, body: z.input<typeof RouteCreate>) =>
    request(`${V1}/networks/${networkId}/routes`, Route, {
      method: 'POST',
      body: RouteCreate.parse(body),
    }),
  // Edit a route → a new version (the prior version stays readable; running instances keep it).
  updateRoute: (networkId: string, name: string, body: z.input<typeof RouteUpdate>) =>
    request(`${V1}/networks/${networkId}/routes/${encodeURIComponent(name)}`, Route, {
      method: 'PUT',
      body: RouteUpdate.parse(body),
    }),
  deleteRoute: (networkId: string, name: string) =>
    request(`${V1}/networks/${networkId}/routes/${encodeURIComponent(name)}`, Empty, {
      method: 'DELETE',
    }),
  // Route instances for a network (StreamRouter in-memory truth — observation, R7/SR14).
  // `state` selects live (default) vs the bounded history of finished ones: an operator asking why
  // a call went badly is asking about an instance that has already ended, and it used to disappear
  // from every surface — with all its numbers — the moment it did.
  routeInstances: (networkId: string, state?: 'completed' | 'errored' | 'all') =>
    request(
      `${V1}/networks/${networkId}/route-instances${state ? `?state=${state}` : ''}`,
      listOf(RouteInstance),
    ),
  // Per-route usage for the graph routes layer (H7): canonical hop chain + directed edges + live
  // usage counters. A gateway that predates this endpoint 404s — callers treat that as "no routes".
  routeUsage: (networkId: string) =>
    request(`${V1}/networks/${networkId}/route-usage`, listOf(RouteUsage)),
  // Drive a route once (records a usage attempt) — a demo/driver hook for the routes layer.
  exerciseRoute: (networkId: string, name: string) =>
    request(`${V1}/networks/${networkId}/routes/${encodeURIComponent(name)}:exercise`, RouteExercise, {
      method: 'POST',
    }),

  // activity
  audit: () => request(`${V1}/audit`, listOf(AuditEvent)),
  calls: () => request(`${V1}/calls`, listOf(CallLog)),
  usage: () => request(`${V1}/usage`, listOf(UsageRecord)),
  // Scoped per-tool usage aggregate (ordered by calls desc). The server scopes to the
  // caller (user → own usage; admin → org-wide), so the dashboard renders it as-is. An
  // optional `project_id` narrows the aggregate to one project.
  usageByTool: (projectId?: string) => {
    const qs = projectId ? `?${new URLSearchParams({ project_id: projectId }).toString()}` : '';
    return request(`${V1}/usage/by-tool${qs}`, listOf(UsageByTool));
  },
  streams: () => request(`${V1}/streams`, listOf(StreamSession)),
  // ST41: mint a short-TTL, read-only consumer token for WS /streams/{id} (the "Watch" action). The
  // WS endpoint requires a per-stream consumer token; this endpoint issues one for the current user,
  // gated by the same stream-visibility rule as GET /streams (403 if the user can't access the
  // stream's network). Returns { stream_id, token, ws_url, expires_in }.
  streamWatchToken: (streamId: string) =>
    request(`${V1}/streams/${encodeURIComponent(streamId)}/watch-token`, StreamWatchToken, {
      method: 'POST',
    }),
  // H49: live /mcp Streamable-HTTP sessions (admin → all; user → own). A gateway that predates this
  // endpoint 404s — the MCP Sessions view treats that as "no sessions / unsupported".
  mcpSessions: () => request(`${V1}/mcp/sessions`, listOf(McpSession)),

  // tools tester — call a tool through /mcp with a chosen token.
  // Returns a unified result: sync result, async task handle, or stream descriptor.
  // The server expects the canonical tool name under `name` (not `canonical_tool`).
  //
  // The live server returns the MCP JSON-RPC envelope ({jsonrpc,id,result:{content,_meta,isError}} or
  // a stream/task shape), while the mock layer returns the {kind} discriminated union directly. We
  // normalize both into ToolCallResult so the tester + test panels render either consistently.
  callTool: async (body: { canonical_tool: string; arguments: unknown; token: string }): Promise<ToolCallResult> => {
    const headers: Record<string, string> = { Accept: 'application/json', 'Content-Type': 'application/json' };
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
    const res = await fetch(apiUrl(`${V1}/tools/call`), {
      method: 'POST',
      headers,
      credentials: bearer ? 'omit' : 'include',
      body: JSON.stringify({ name: body.canonical_tool, arguments: body.arguments, token: body.token }),
    });
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      const msg = json && typeof json === 'object' && 'message' in json ? String((json as { message: unknown }).message) : `HTTP ${res.status}`;
      throw new ApiError(res.status, msg, json);
    }
    const normalized = normalizeToolCall(json);
    const parsed = ToolCallResult.safeParse(stripNulls(normalized));
    if (!parsed.success) {
      throw new ApiError(res.status, 'response schema mismatch for /tools/call', parsed.error.issues);
    }
    return parsed.data;
  },
};

// Map the server's tool-call response onto the ToolCallResult union. Accepts the MCP JSON-RPC envelope,
// a bare MCP result, or the mock's already-shaped {kind} union.
function normalizeToolCall(raw: unknown): unknown {
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if (typeof o.kind === 'string') return o; // already the union (mock layer)
    if (o.error && typeof o.error === 'object') {
      const e = o.error as Record<string, unknown>;
      throw new ApiError(400, String(e.message ?? 'tool call error'), e);
    }
    const inner = (o.result && typeof o.result === 'object' ? o.result : o) as Record<string, unknown>;
    // streaming descriptor (SPEC §5A) — carries a stream id + ws url.
    if (typeof inner.stream_id === 'string' || typeof inner.ws_url === 'string') {
      return { kind: 'stream', channels: [], ...inner };
    }
    // async task handle.
    if (typeof inner.task_id === 'string' && inner.content === undefined && inner._meta === undefined) {
      return { kind: 'task', task_id: inner.task_id, state: inner.state ?? 'QUEUED' };
    }
    // sync result: keep the full MCP result as `output` (the renderer reads content/_meta from it).
    const meta = (inner._meta && typeof inner._meta === 'object' ? inner._meta : {}) as Record<string, unknown>;
    return { kind: 'result', output: inner, usage: meta.usage, task_id: meta.task_id };
  }
  return { kind: 'result', output: raw };
}

export type Api = typeof api;
